/** Measured RFIMS history; no synthetic or embedded example fallback. */
import { fmtLoad, fmtMs, fmtPct, fmtTemp, gb, isLiveSite, num } from './influxBridge';
import type { LiveDataSource, LiveHistoryResponse } from './types';
import { isExcludedRfimsWindow } from '@/features/rfims/historyProvenance';
import { f200State } from '@/features/rfims/f200';

export interface LiveHistorySeries {
  /** Metric name on /api/live/history. */
  metric: string;
  fmt: (v: number) => string;
  unit?: string;
}

/** Keyed `${subsystemId}.${dataId}`, the store's reading key. */
export const LIVE_HISTORY: Record<string, LiveHistorySeries> = {
  'environmental.temperature': { metric: 'temperature_c', fmt: fmtTemp, unit: '°C' },
  'environmental.humidity': { metric: 'humidity_pct', fmt: fmtPct, unit: '%' },
  'host_health.cpu_load': { metric: 'load_5min', fmt: fmtLoad },
  'host_health.disk_usage': { metric: 'disk_used_mib', fmt: (v) => gb(v) + ' GiB', unit: 'GiB' },
  'network.latency': { metric: 'ping_latency_ms', fmt: fmtMs, unit: 'ms' },
  'network.packet_loss': { metric: 'ping_packet_loss_pct', fmt: fmtPct, unit: '%' },
  'environmental.door': { metric: 'door_open', fmt: v => f200State('input', v) },
  'environmental.leak': { metric: 'leak_alarm', fmt: v => f200State('leak', v) },
  'environmental.relay': { metric: 'relay_state', fmt: v => f200State('relay', v) },
  'host_health.uptime': { metric: 'uptime_min', fmt: v => `${v} min`, unit: 'min' },
};

export const DEFAULT_HISTORY_DAYS = 30;

export function liveHistorySeries(subsystemId: string, dataId: string): LiveHistorySeries | undefined {
  return LIVE_HISTORY[`${subsystemId}.${dataId}`];
}

/** Values in chart space have already been converted from MiB to GiB. */
export function formatHistoryNumeric(subsystemId:string,dataId:string,value:number):string {
  const series=liveHistorySeries(subsystemId,dataId);
  return series?.metric==='disk_used_mib'?`${value.toFixed(1)} GiB`:series?series.fmt(value):String(value);
}

/** True only for a live site AND a chartable reading (the original ignored the site). */
export function hasLiveHistory(elementId: string | null | undefined, subsystemId: string, dataId: string): boolean {
  return isLiveSite(elementId) && liveHistorySeries(subsystemId, dataId) !== undefined;
}

export interface LiveHistoryPoint {
  datetime: string;
  /** Formatted like the card value, e.g. "22.4°C". */
  value: string;
  numeric: number;
}

export interface LiveHistory {
  site: string;
  metric: string;
  days: number;
  source: LiveDataSource;
  degraded?: string;
  /** Newest first. */
  points: LiveHistoryPoint[];
}

export function historyUrl(site: string, metric: string, days: number): string {
  return `/api/live/history?site=${encodeURIComponent(site)}&metric=${encodeURIComponent(metric)}&days=${days}`;
}

/**
 * Null when the element is not a live site or the reading has no series;
 * throws on an HTTP error or a malformed body so the caller can show it.
 */
export async function fetchLiveHistory(
  elementId: string | null | undefined,
  subsystemId: string,
  dataId: string,
  days: number = DEFAULT_HISTORY_DAYS,
  fetchImpl: typeof fetch = (input, init) => fetch(input, init),
): Promise<LiveHistory | null> {
  if (!hasLiveHistory(elementId, subsystemId, dataId)) return null;
  const series = liveHistorySeries(subsystemId, dataId)!;
  const url = historyUrl(elementId as string, series.metric, days);
  const res = await fetchImpl(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!res.ok) throw new Error(`HTTP ${res.status} fetching ${url}`);
  const data = (await res.json()) as LiveHistoryResponse;
  if (!data || !Array.isArray(data.points)) throw new Error(`unexpected history body from ${url}`);
  if (data._source !== 'influxdb') throw new Error('Measured history is unavailable');
  if (data.site !== elementId || data.metric !== series.metric || data.days !== days) throw new Error('History response does not match the requested site, metric and range');
  const points = data.points
    .filter((p) => p && Number.isFinite(Date.parse(p.datetime)) && Date.parse(p.datetime) <= Date.now() + 30_000 && num(p.value))
    .filter((p) => !isExcludedRfimsWindow(Date.parse(p.datetime), (days <= 2 ? 5 : days <= 7 ? 30 : days <= 31 ? 120 : 360) * 60_000))
    .map((p) => ({ datetime: p.datetime, value: series.fmt(p.value), numeric: series.metric === 'disk_used_mib' ? p.value / 1024 : p.value }))
    .reverse();
  return { site: data.site, metric: data.metric, days: data.days, source: data._source, degraded: data._degraded?.reason, points };
}
